import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Container, Table, Form, InputGroup, Button, Row, Col, Pagination, Badge, Modal, Spinner, Alert } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import distributorService from '../../../../services/DistributorService';
import { showToast } from '../../../../utils/toast';

const DistributorBeats = () => {
  const [beatsList, setBeatsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedBeat, setSelectedBeat] = useState(null);
  
  const [formData, setFormData] = useState({
    beat_name: ''
  });
  
  const [formErrors, setFormErrors] = useState({});

  // Debounce timer reference
  const [searchTimeoutId, setSearchTimeoutId] = useState(null);

  // Fetch Beats from API
  const fetchBeats = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        page_size: rowsPerPage
      };
      
      if (searchQuery) params.search = searchQuery;
      
      const response = await distributorService.getBeats(params);
      
      if (response.success) {
        setBeatsList(response.data.results || []);
        setTotalCount(response.data.count || 0);
      } else {
        showToast.error(response.error || 'Failed to fetch beats');
      }
    } catch (error) {
      console.error('Error fetching beats:', error);
      showToast.error('Failed to fetch beats');
    } finally {
      setLoading(false);
    }
  };

  // Debounced fetch function
  const debouncedFetchBeats = useCallback(() => {
    if (searchTimeoutId) {
      clearTimeout(searchTimeoutId);
    }
    
    const timeoutId = setTimeout(() => {
      fetchBeats();
    }, 300); // 300ms debounce
    
    setSearchTimeoutId(timeoutId);
  }, [currentPage, rowsPerPage, searchQuery]);

  useEffect(() => {
    if (searchQuery) {
      debouncedFetchBeats();
    } else {
      fetchBeats();
    }
  }, [currentPage, rowsPerPage, searchQuery]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutId) {
        clearTimeout(searchTimeoutId);
      }
    };
  }, [searchTimeoutId]);

  const totalPages = Math.ceil(totalCount / rowsPerPage);
  
  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };


  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(beatsList);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Beats');
    XLSX.writeFile(workbook, `beats_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleAdd = () => {
    setSelectedBeat(null);
    setModalMode('add');
    setFormData({
      beat_name: ''
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (beat) => {
    setSelectedBeat(beat);
    setModalMode('edit');
    setFormData({
      beat_name: beat.beat_name || ''
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (beat) => {
    if (window.confirm(`Are you sure you want to delete "${beat.beat_name}"?`)) {
      try {
        const response = await distributorService.deleteBeat(beat.id);
        if (response.success) {
          showToast.success('Beat deleted successfully');
          fetchBeats();
        } else {
          showToast.error(response.error || 'Failed to delete beat');
        }
      } catch (error) {
        console.error('Error deleting beat:', error);
        showToast.error('Failed to delete beat');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormErrors({});
    
    try {
      const payload = {
        beat_name: formData.beat_name.trim()
      };
      
      let response;
      if (modalMode === 'add') {
        response = await distributorService.createBeat(payload);
      } else {
        response = await distributorService.updateBeat(selectedBeat.id, payload);
      }
      
      if (response.success) {
        showToast.success(response.message || `Beat ${modalMode === 'add' ? 'created' : 'updated'} successfully`);
        setShowModal(false);
        fetchBeats();
      } else {
        showToast.error(response.error || `Failed to ${modalMode === 'add' ? 'create' : 'update'} beat`);
      }
    } catch (error) {
      console.error('Error saving beat:', error);
      
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      } else if (error.response?.data?.message) {
        showToast.error(error.response.data.message);
      } else {
        showToast.error(`Failed to ${modalMode === 'add' ? 'create' : 'update'} beat`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Container fluid className="py-1">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 style={{ color: '#031a6f', fontWeight: '600' }}>
          <i className="fas fa-map-marker-alt me-2"></i>
          Beat Management
        </h5>
        <div className="d-flex gap-2">
          <Button variant="success" onClick={exportToExcel}>
            <i className="fas fa-file-excel me-2"></i>
            Export Excel
          </Button>
          <Button variant="primary" onClick={handleAdd}>
            <i className="fas fa-plus me-2"></i>
            Add Beat
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Row className="mb-4">
        <Col md={3}>
          <InputGroup>
            <InputGroup.Text>
              <i className="fas fa-search"></i>
            </InputGroup.Text>
            <Form.Control
              placeholder="Search beats..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={9}>
          {/* Space for future filters */}
        </Col>
      </Row>

      {/* Table */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <div className="table-responsive">
          <Table striped bordered hover className="modern-table">
            <thead>
              <tr>
                <th>Actions</th>
                <th>Beat ID</th>
                <th>Beat Name</th>
                <th>Retailers Count</th>
              </tr>
            </thead>
            <tbody>
              {beatsList.map((beat) => (
                <tr key={beat.id}>
                  <td>
                    <div className="d-flex gap-1">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEdit(beat)}
                      >
                        <i className="fas fa-edit"></i>
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(beat)}
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    </div>
                  </td>
                  <td><strong>{beat.id}</strong></td>
                  <td>
                    <div>
                      <div className="fw-bold">{beat.beat_name}</div>
                    </div>
                  </td>
                  <td>
                    <Badge bg="info">
                      {beat.retailer_count || 0} Retailers
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          
          {beatsList.length === 0 && (
            <div className="text-center py-4">
              <p className="text-muted">No beats found</p>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div>
          <Form.Select
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
            style={{ width: 'auto' }}
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
          </Form.Select>
        </div>
        <div>
          Showing {((currentPage - 1) * rowsPerPage) + 1} to {Math.min(currentPage * rowsPerPage, totalCount)} of {totalCount} entries
        </div>
        <Pagination>
          <Pagination.First 
            onClick={() => setCurrentPage(1)} 
            disabled={currentPage === 1} 
          />
          <Pagination.Prev 
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
            disabled={currentPage === 1} 
          />
          {[...Array(Math.min(5, totalPages))].map((_, index) => {
            const pageNum = Math.max(1, Math.min(currentPage - 2 + index, totalPages - 4 + index));
            return (
              <Pagination.Item
                key={pageNum}
                active={pageNum === currentPage}
                onClick={() => setCurrentPage(pageNum)}
              >
                {pageNum}
              </Pagination.Item>
            );
          })}
          <Pagination.Next 
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
            disabled={currentPage === totalPages} 
          />
          <Pagination.Last 
            onClick={() => setCurrentPage(totalPages)} 
            disabled={currentPage === totalPages} 
          />
        </Pagination>
      </div>

      {/* Beat Form Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="md">
        <Modal.Header closeButton>
          <Modal.Title>{modalMode === 'add' ? 'Add New' : 'Edit'} Beat</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {Object.keys(formErrors).length > 0 && (
              <Alert variant="danger">
                <ul className="mb-0">
                  {Object.entries(formErrors).map(([field, errors]) => (
                    <li key={field}>{field}: {Array.isArray(errors) ? errors.join(', ') : errors}</li>
                  ))}
                </ul>
              </Alert>
            )}
            
            <Row>
              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label>Beat Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.beat_name}
                    onChange={(e) => handleInputChange('beat_name', e.target.value)}
                    placeholder="Enter beat name (e.g., South Zone Beat)"
                    isInvalid={formErrors.beat_name}
                    required
                  />
                  <Form.Text className="text-muted">
                    Enter a descriptive name for the beat
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {modalMode === 'add' ? 'Creating...' : 'Updating...'}
                </>
              ) : (
                <>
                  {modalMode === 'add' ? 'Create' : 'Update'} Beat
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

    </Container>
  );
};

export default DistributorBeats;